import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { 
    ArrowLeft, Bus, Calendar, Clock, MapPin, Users, 
    ChevronRight, CheckCircle2, AlertCircle, Loader2, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function TransportPage() {
    const navigate = useNavigate();
    const { organization } = useOrganization();
    const { token } = useAuth();
    const [loading, setLoading] = useState(true);
    const [trips, setTrips] = useState([]);
    const [myBookings, setMyBookings] = useState([]);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [bookingModal, setBookingModal] = useState(false);
    const [cancelModal, setCancelModal] = useState({ open: false, booking: null });
    const [actionLoading, setActionLoading] = useState(false);

    const headers = useCallback(() => token ? { Authorization: `Bearer ${token}` } : {}, [token]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [tripsRes, bookingsRes] = await Promise.all([
                axios.get(`${API}/transport/trips`, { headers: headers() }),
                axios.get(`${API}/transport/my-bookings`, { headers: headers() })
            ]);
            setTrips(tripsRes.data);
            setMyBookings(bookingsRes.data);
        } catch (error) {
            console.error('Error fetching transport data:', error);
            toast.error('Failed to load trips');
        } finally {
            setLoading(false);
        }
    }, [headers]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleBookTrip = async () => {
        if (!selectedTrip) return;
        
        setActionLoading(true);
        try {
            const response = await axios.post(
                `${API}/transport/trips/${selectedTrip.id}/book`,
                { trip_id: selectedTrip.id },
                { headers: headers() }
            );
            toast.success(response.data.message);
            setBookingModal(false);
            setSelectedTrip(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to book trip');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCancelBooking = async () => {
        if (!cancelModal.booking) return;
        
        setActionLoading(true);
        try {
            await axios.delete(
                `${API}/transport/bookings/${cancelModal.booking.id}`,
                { headers: headers() }
            );
            toast.success('Booking cancelled successfully');
            setCancelModal({ open: false, booking: null });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to cancel booking');
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            weekday: 'long', day: 'numeric', month: 'long'
        });
    };

    const formatShortDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            weekday: 'short', day: 'numeric', month: 'short'
        });
    };

    const getStatusBadge = (trip) => {
        if (trip.user_booked) {
            if (trip.user_booking_status === 'waitlisted') {
                return (
                    <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Waitlist #{trip.user_waitlist_position}
                    </span>
                );
            }
            return (
                <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    Booked
                </span>
            );
        }
        if (trip.available_seats === 0) {
            return (
                <span className="text-xs px-2 py-1 rounded-full bg-stone-100 text-stone-600">
                    Full - Join Waitlist
                </span>
            );
        }
        return (
            <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">
                {trip.available_seats} seats available
            </span>
        );
    };

    return (
        <div className="min-h-screen bg-stone-50 pb-24" data-testid="transport-page">
            {/* Header */}
            <div className="bg-gradient-to-br from-teal-700 to-teal-800 text-white px-6 pt-6 pb-8">
                <button 
                    onClick={() => navigate('/home')}
                    className="flex items-center gap-2 text-teal-200 hover:text-white mb-4"
                    data-testid="transport-back-btn"
                >
                    <ArrowLeft className="w-5 h-5" />
                    Home
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                        <Bus className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-serif text-white">Minibus Trips</h1>
                        <p className="text-white/80 text-sm">Book your seat on our community outings</p>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="px-4 -mt-4 relative z-10">
                {/* My Bookings Section */}
                {myBookings.length > 0 && (
                    <div className="mb-6">
                        <h2 className="text-lg font-semibold text-stone-800 mb-3 mt-6">My Bookings</h2>
                        <div className="space-y-3">
                            {myBookings.map((booking) => (
                                <div 
                                    key={booking.id} 
                                    className="bg-white rounded-2xl p-4 border border-stone-200 shadow-sm"
                                    data-testid={`my-booking-${booking.id}`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className={`text-xs px-2 py-1 rounded-full ${
                                                    booking.status === 'confirmed' 
                                                        ? 'bg-green-100 text-green-700' 
                                                        : 'bg-amber-100 text-amber-700'
                                                }`}>
                                                    {booking.status === 'confirmed' ? 'Confirmed' : `Waitlist #${booking.waitlist_position}`}
                                                </span>
                                            </div>
                                            <h3 className="font-semibold text-stone-800">{booking.trip?.title}</h3>
                                            <p className="text-sm text-teal-700 font-medium mt-1">
                                                {booking.trip?.date ? formatShortDate(booking.trip.date) : ''} at {booking.trip?.departure_time}
                                            </p>
                                            <p className="text-xs text-stone-500 mt-1 flex items-center gap-1">
                                                <MapPin className="w-3 h-3" />
                                                {booking.trip?.destination}
                                            </p>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                            onClick={() => setCancelModal({ open: true, booking })}
                                            data-testid={`cancel-booking-${booking.id}`}
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Upcoming Trips */}
                <h2 className="text-lg font-semibold text-stone-800 mb-3 mt-6">Upcoming Trips</h2>
                
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : trips.length === 0 ? (
                    <div className="bg-stone-100 rounded-2xl p-8 text-center">
                        <Bus className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                        <p className="text-stone-500">No upcoming trips scheduled</p>
                        <p className="text-stone-400 text-sm mt-2">Check back soon for new outings!</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {trips.map((trip) => (
                            <div 
                                key={trip.id} 
                                className={`bg-white rounded-2xl p-4 border transition-all cursor-pointer ${
                                    trip.user_booked 
                                        ? 'border-teal-300 bg-teal-50/30' 
                                        : 'border-stone-200 hover:border-teal-200'
                                }`}
                                onClick={() => {
                                    if (!trip.user_booked) {
                                        setSelectedTrip(trip);
                                        setBookingModal(true);
                                    }
                                }}
                                data-testid={`trip-card-${trip.id}`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            {getStatusBadge(trip)}
                                        </div>
                                        <h3 className="font-semibold text-stone-800 text-lg">{trip.title}</h3>
                                        {trip.description && (
                                            <p className="text-sm text-stone-600 mt-1">{trip.description}</p>
                                        )}
                                        
                                        <div className="mt-3 space-y-1">
                                            <p className="text-sm text-stone-700 flex items-center gap-2">
                                                <Calendar className="w-4 h-4 text-teal-600" />
                                                {formatDate(trip.date)}
                                            </p>
                                            <p className="text-sm text-stone-700 flex items-center gap-2">
                                                <Clock className="w-4 h-4 text-teal-600" />
                                                Departs {trip.departure_time}
                                                {trip.return_time && ` • Returns ~${trip.return_time}`}
                                            </p>
                                            <p className="text-sm text-stone-700 flex items-center gap-2">
                                                <MapPin className="w-4 h-4 text-teal-600" />
                                                {trip.pickup_location} → {trip.destination}
                                            </p>
                                            <p className="text-sm text-stone-500 flex items-center gap-2">
                                                <Users className="w-4 h-4" />
                                                {trip.booked_seats}/{trip.total_seats} seats booked
                                                {trip.waitlist_count > 0 && ` • ${trip.waitlist_count} on waitlist`}
                                            </p>
                                        </div>
                                    </div>
                                    {!trip.user_booked && (
                                        <ChevronRight className="w-5 h-5 text-stone-400" />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Info Box */}
                <div className="mt-6 bg-teal-50 rounded-2xl p-5 border border-teal-200">
                    <h3 className="font-semibold text-teal-800 mb-2">About Our Trips</h3>
                    <ul className="text-sm text-teal-700 space-y-2">
                        <li>• All trips depart from the main entrance</li>
                        <li>• Please arrive 10 minutes before departure</li>
                        <li>• Cancellations must be made 24+ hours in advance</li>
                        <li>• If the minibus is full, you can join the waitlist</li>
                    </ul>
                </div>
            </div>

            {/* Booking Modal */}
            <Dialog open={bookingModal} onOpenChange={(open) => { if (!open) { setBookingModal(false); setSelectedTrip(null); }}}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Book Your Seat</DialogTitle>
                        <DialogDescription>
                            Reserve your place on this trip
                        </DialogDescription>
                    </DialogHeader>
                    
                    {selectedTrip && (
                        <div className="space-y-4">
                            <div className="bg-teal-50 rounded-xl p-4">
                                <h3 className="font-semibold text-stone-800">{selectedTrip.title}</h3>
                                <div className="mt-2 space-y-1 text-sm">
                                    <p className="text-stone-700">
                                        <strong>Date:</strong> {formatDate(selectedTrip.date)}
                                    </p>
                                    <p className="text-stone-700">
                                        <strong>Departure:</strong> {selectedTrip.departure_time}
                                    </p>
                                    <p className="text-stone-700">
                                        <strong>Pickup:</strong> {selectedTrip.pickup_location}
                                    </p>
                                    <p className="text-stone-700">
                                        <strong>Destination:</strong> {selectedTrip.destination}
                                    </p>
                                </div>
                            </div>
                            
                            {selectedTrip.available_seats === 0 ? (
                                <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
                                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="font-medium">Trip is Full</p>
                                        <p className="text-xs mt-1">
                                            You'll be added to the waitlist. We'll notify you if a seat becomes available.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg text-sm text-green-700">
                                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="font-medium">{selectedTrip.available_seats} Seats Available</p>
                                        <p className="text-xs mt-1">
                                            Your booking will be confirmed immediately.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setBookingModal(false); setSelectedTrip(null); }}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleBookTrip} 
                            disabled={actionLoading}
                            className="bg-teal-600 hover:bg-teal-700"
                            data-testid="confirm-booking-btn"
                        >
                            {actionLoading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Booking...
                                </>
                            ) : selectedTrip?.available_seats === 0 ? (
                                'Join Waitlist'
                            ) : (
                                'Confirm Booking'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Cancel Modal */}
            <Dialog open={cancelModal.open} onOpenChange={(open) => !open && setCancelModal({ open: false, booking: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Cancel Booking</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to cancel this booking?
                        </DialogDescription>
                    </DialogHeader>
                    
                    {cancelModal.booking && (
                        <div className="bg-stone-50 rounded-xl p-4">
                            <h3 className="font-semibold text-stone-800">{cancelModal.booking.trip?.title}</h3>
                            <p className="text-sm text-stone-600 mt-1">
                                {cancelModal.booking.trip?.date ? formatShortDate(cancelModal.booking.trip.date) : ''} at {cancelModal.booking.trip?.departure_time}
                            </p>
                        </div>
                    )}
                    
                    <p className="text-sm text-stone-600">
                        Note: Cancellations must be made at least 24 hours before departure.
                    </p>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCancelModal({ open: false, booking: null })}>
                            Keep Booking
                        </Button>
                        <Button 
                            variant="destructive"
                            onClick={handleCancelBooking}
                            disabled={actionLoading}
                            data-testid="confirm-cancel-btn"
                        >
                            {actionLoading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Cancelling...
                                </>
                            ) : (
                                'Cancel Booking'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default TransportPage;
